import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { GetJWTHandlerPayload } from '@hqo/hqo-miniapp-client-sdk';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk';
import { useApi } from 'app/shared/contexts/api';
import { useErrors } from 'app/shared/contexts/errors';
import { ERROR_PATH } from 'app/shared/consts/paths';

const UNAUTHORIZED_STATUS = 401;

// One refresh per window — stops a dead session looping 401 -> refresh -> 401.
const REFRESH_COOLDOWN_MS = 5000;

const isUnauthorizedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('statusCode' in error)) {
    return false;
  }

  return (error as { statusCode: unknown }).statusCode === UNAUTHORIZED_STATUS;
};

/**
 * Keeps the embedded miniapp's auth token fresh. The host silently re-auths
 * while a tab is backgrounded, leaving the launch token expired; this asks the
 * host for a new one (on refocus, or when a query 401s) and threads it back in.
 */
export const useTokenRefresh = (): void => {
  const sdk = useMiniappSdk();
  const setAuthToken = useApi('setAuthToken');
  const queryClient = useQueryClient();
  const { hideGenericErrors } = useErrors();
  const navigate = useNavigate();
  const lastRefreshRef = useRef(0);

  const requestTokenRefresh = useCallback(() => {
    if (!sdk) {
      return;
    }

    const now = Date.now();
    if (now - lastRefreshRef.current < REFRESH_COOLDOWN_MS) {
      return;
    }

    lastRefreshRef.current = now;
    sdk.refreshToken();
  }, [sdk]);

  // Host delivered a fresh token: store it, refetch, and unstick the error screen.
  useMiniappSdkEventHandler(
    'refreshTokenResponse',
    (eventData) => {
      const { token } = (eventData ?? {}) as Partial<GetJWTHandlerPayload>;

      if (!token) {
        return;
      }

      setAuthToken(token);
      hideGenericErrors();
      queryClient.invalidateQueries();

      if (window.location.pathname.endsWith(ERROR_PATH)) {
        navigate('/', { replace: true });
      }
    },
    [setAuthToken, hideGenericErrors, queryClient, navigate],
  );

  // Trigger 1: tab regained focus after being backgrounded.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestTokenRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [requestTokenRefresh]);

  // Trigger 2: safety net for any other expiry path — a query 401s.
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (isUnauthorizedError(event?.query?.state?.error)) {
        requestTokenRefresh();
      }
    });

    return unsubscribe;
  }, [queryClient, requestTokenRefresh]);
};
